import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { NavTree } from '../components/nav/NavTree';
import { TipTapEditor } from '../components/editor/TipTapEditor';
import { MarkdownPreview } from '../components/preview/MarkdownPreview';
import { useAuth } from '../hooks/useAuth';
import { useContent } from '../hooks/useContent';
import { useDraft } from '../hooks/useDraft';
import { fetchNav } from '../lib/api';
import type { NavNode } from '../lib/types';

interface PublishDialogProps {
  path: string;
  onConfirm: (message: string) => void;
  onClose: () => void;
  publishing: boolean;
}

const PublishDialog: React.FC<PublishDialogProps> = ({
  path,
  onConfirm,
  onClose,
  publishing,
}) => {
  const [message, setMessage] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Publish Page</h2>
        <p className="text-sm text-gray-600 mb-4">
          This will commit your changes to{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">{path}</code> on the{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">main</code> branch.
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Commit Message (optional)
          </label>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Update ${path} via research editor`}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') onConfirm(message);
            }}
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={publishing}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(message)}
            disabled={publishing}
            className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 font-medium"
          >
            {publishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

function formatDraftAge(savedAt: string | null): string {
  if (!savedAt) return '';
  const diff = Date.now() - new Date(savedAt).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  return `${minutes} minutes ago`;
}

export const EditorPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { content, loading: contentLoading, error: contentError, load, publish, publishing } =
    useContent();

  const [navNodes, setNavNodes] = useState<NavNode[]>([]);
  const [navLoading, setNavLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  const [splitterPos, setSplitterPos] = useState(50); // percent
  const splitterDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Stable ref for getMarkdown to avoid stale closures in useDraft
  const markdownRef = useRef(markdown);
  markdownRef.current = markdown;
  const getMarkdown = useCallback(() => markdownRef.current, []);

  const { draftSavedAt, isDirty, markDirty, markClean, forceSave } = useDraft(
    currentPath,
    getMarkdown
  );

  // Load nav
  useEffect(() => {
    fetchNav()
      .then((data) => setNavNodes(data.nav))
      .catch(console.error)
      .finally(() => setNavLoading(false));
  }, []);

  // Handle page selection
  const handleSelectPage = useCallback(
    async (path: string) => {
      if (path === currentPath) return;

      // Auto-save current draft before switching
      if (currentPath && isDirty) {
        await forceSave(currentPath, markdownRef.current);
      }

      setCurrentPath(path);
      setMarkdown('');
      setPublishSuccess(null);
    },
    [currentPath, isDirty, forceSave]
  );

  // Load content when path changes
  useEffect(() => {
    if (currentPath) {
      load(currentPath);
    }
  }, [currentPath, load]);

  // Set markdown from loaded content (prefer draft)
  useEffect(() => {
    if (content) {
      const initial = content.draft ?? content.content;
      setMarkdown(initial);
      if (content.draft) {
        // Has an existing draft — mark as having saved draft info
      }
    }
  }, [content]);

  const handleMarkdownChange = useCallback(
    (newMarkdown: string) => {
      setMarkdown(newMarkdown);
      markDirty();
    },
    [markDirty]
  );

  const handlePublish = async (message: string) => {
    if (!currentPath) return;
    try {
      const result = await publish(currentPath, markdown, message || undefined);
      markClean();
      setShowPublishDialog(false);
      setPublishSuccess(`Published! Commit: ${result.commitSha.slice(0, 7)}`);
    } catch (err) {
      alert(`Publish failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Splitter drag
  const handleSplitterMouseDown = useCallback(() => {
    splitterDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!splitterDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pos = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitterPos(Math.max(20, Math.min(80, pos)));
    };

    const handleMouseUp = () => {
      splitterDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const breadcrumbs = currentPath ? currentPath.split('/') : [];

  return (
    <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-4 flex-shrink-0 z-10">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-7 h-7 bg-blue-600 rounded flex items-center justify-center">
            <span className="text-white text-xs font-bold">R</span>
          </div>
          <span className="font-semibold text-gray-900 text-sm">Research Editor</span>
        </div>

        {/* Breadcrumb */}
        {currentPath && (
          <nav className="flex items-center gap-1 text-sm text-gray-500 overflow-hidden">
            <span>docs</span>
            {breadcrumbs.map((seg, idx) => (
              <React.Fragment key={idx}>
                <span>/</span>
                <span
                  className={
                    idx === breadcrumbs.length - 1 ? 'text-gray-800 font-medium' : ''
                  }
                >
                  {seg}
                </span>
              </React.Fragment>
            ))}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-3">
          {/* Status */}
          <div className="text-xs text-gray-500">
            {publishSuccess ? (
              <span className="text-green-600 font-medium">{publishSuccess}</span>
            ) : isDirty ? (
              <span className="text-amber-600">Unsaved changes</span>
            ) : draftSavedAt ? (
              <span>Draft saved {formatDraftAge(draftSavedAt)}</span>
            ) : null}
          </div>

          {/* Publish Button */}
          {currentPath && (
            <button
              onClick={() => setShowPublishDialog(true)}
              disabled={publishing}
              className="px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          )}

          {user?.role === 'admin' && (
            <Link
              to="/admin"
              className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
            >
              Admin
            </Link>
          )}

          {user && (
            <div className="flex items-center gap-2">
              {user.picture && (
                <img
                  src={user.picture}
                  alt=""
                  className="w-7 h-7 rounded-full"
                />
              )}
              <span className="text-xs text-gray-600 hidden sm:block">{user.name}</span>
            </div>
          )}

          <button
            onClick={logout}
            className="text-sm text-gray-500 hover:text-gray-800 hover:underline"
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-60 flex-shrink-0 bg-sidebar overflow-y-auto flex flex-col">
          <div className="px-3 py-2 border-b border-sidebar-hover">
            <h2 className="text-xs font-semibold text-sidebar-muted uppercase tracking-wider">
              Pages
            </h2>
          </div>
          {navLoading ? (
            <div className="px-4 py-4 text-sidebar-muted text-xs">Loading nav...</div>
          ) : (
            <NavTree
              nodes={navNodes}
              currentPath={currentPath}
              onSelect={handleSelectPage}
            />
          )}
        </aside>

        {/* Editor + Preview Panel */}
        {!currentPath ? (
          <div className="flex-1 flex items-center justify-center bg-white">
            <div className="text-center text-gray-400">
              <div className="text-4xl mb-3">📄</div>
              <p className="text-sm">Select a page from the sidebar to start editing</p>
            </div>
          </div>
        ) : (
          <div ref={containerRef} className="flex flex-1 overflow-hidden">
            {/* Editor Panel */}
            <div
              className="flex flex-col overflow-hidden bg-white border-r border-gray-200"
              style={{ width: `${splitterPos}%` }}
            >
              {contentLoading ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                  Loading...
                </div>
              ) : contentError ? (
                <div className="flex-1 flex items-center justify-center text-red-500 text-sm px-6 text-center">
                  {contentError}
                </div>
              ) : (
                <TipTapEditor
                  key={currentPath}
                  initialMarkdown={content?.draft ?? content?.content ?? ''}
                  onChange={handleMarkdownChange}
                  placeholder="Start writing..."
                />
              )}
            </div>

            {/* Splitter */}
            <div
              className="w-1 cursor-col-resize bg-gray-200 hover:bg-blue-400 flex-shrink-0 transition-colors"
              onMouseDown={handleSplitterMouseDown}
            />

            {/* Preview Panel */}
            <div
              className="overflow-hidden bg-white"
              style={{ width: `${100 - splitterPos}%` }}
            >
              <div className="h-full overflow-auto">
                <div className="px-2 py-1.5 border-b border-gray-200 bg-gray-50">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preview
                  </span>
                </div>
                <MarkdownPreview markdown={markdown} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Publish Dialog */}
      {showPublishDialog && currentPath && (
        <PublishDialog
          path={currentPath}
          onConfirm={handlePublish}
          onClose={() => setShowPublishDialog(false)}
          publishing={publishing}
        />
      )}
    </div>
  );
};
